import { DefaultTooltipContentProps } from 'recharts';
import { DeltaInfo } from '../hooks/useChartDataWithDelta';
import { RechartsNameType, RechartsValueType } from '../types';
import { DeltaIndicator } from '../../../atoms/DeltaIndicator/DeltaIndicator';
/**
 * Props including delta information.
 */
type ChartTooltipContentPropsExtended = DefaultTooltipContentProps<
  RechartsValueType,
  RechartsNameType
>;

/**
 * Type guard for delta information
 */
function isDeltaInfo(value: unknown): value is DeltaInfo {
  return Boolean(
    value &&
    typeof value === 'object' &&
    Object.values(value).every(
      (item) =>
        item &&
        typeof item === 'object' &&
        'delta' in item &&
        typeof item.delta === 'number',
    ),
  );
}

/**
 * Renders delta information for a chart tooltip entry.
 */
function renderDeltaInfo(entry: {
  payload?: { delta?: Record<string, DeltaInfo> };
  dataKey?: string | number | undefined | unknown;
}): React.ReactNode {
  const delta = entry.payload?.delta;
  const dataKey = entry.dataKey;

  if (typeof dataKey !== 'string' || !isDeltaInfo(delta)) {
    return null;
  }

  const deltaInfo = delta[dataKey];

  if (!deltaInfo) {
    return null;
  }

  return <DeltaIndicator {...deltaInfo} />;
}

/**
 * Custom Tooltip content that includes delta information.
 *
 * @template TValue - The type of the tooltip value.
 * @template TName - The type of the tooltip name.
 *
 * @param props - The tooltip properties.
 * @returns A React node representing the tooltip content.
 */
export const ChartTooltipContent = ({
  // active, // TODO: Validate this
  payload,
  label,
  ...props
}: ChartTooltipContentPropsExtended) => {
  if (!(payload && payload.length)) {
    return null;
  }

  const labelRender = label
    ? props.labelFormatter
      ? props.labelFormatter(label, payload)
      : label
    : payload[0]?.payload.name;

  const isSinglePayload = payload.length === 1;

  return (
    <div
      data-testid="chart-tooltip-content"
      className="overflow-hidden rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 shadow-sm"
    >
      {!isSinglePayload && (
        <div
          data-testid="chart-tooltip-label"
          className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-2 px-4 text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          {labelRender}
        </div>
      )}
      <div
        className={`flex ${isSinglePayload ? 'flex-row items-center' : 'flex-col'} gap-2 p-2 px-4`}
      >
        {isSinglePayload && (
          <div
            data-testid="chart-tooltip-label"
            className="mr-2 text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            {labelRender}
          </div>
        )}
        {payload.map((entry, index) => {
          const valueRender = props.formatter
            ? props.formatter(entry.value!, entry.name!, entry, index, payload)
            : [entry.value, entry.name];

          if (!Array.isArray(valueRender)) {
            return valueRender;
          }

          const [value, name] = valueRender;

          return (
            <div
              key={`item-${index}`}
              className={`flex items-center ${isSinglePayload ? 'gap-10' : 'justify-between gap-10'}`}
            >
              {!isSinglePayload && (
                <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                  <div
                    className="mr-2 h-3 w-3 flex-shrink-0 truncate rounded border-2 border-gray-50 whitespace-pre drop-shadow"
                    style={{
                      backgroundColor: entry.color || entry.payload?.fill,
                    }}
                  />
                  <div className="max-w-[250px] truncate overflow-ellipsis whitespace-nowrap">
                    {name}
                  </div>
                </div>
              )}
              <div
                data-testid="chart-tooltip-value"
                className="ml-auto flex items-center gap-2 text-sm font-medium"
              >
                {value}
                {renderDeltaInfo(entry)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
